import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Select } from '../components/UI';
import T from '../styles/theme';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import supabase from '../data/supabase';
import apiFetch from '../data/api';

export default function SMSPage({ isMobile }) {
  const { contacts: CONTACTS, teamMembers: TEAM_MEMBERS } = useStore();
  const { currentUser } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [smsBody, setSmsBody] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [newMessageTo, setNewMessageTo] = useState("");
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [search, setSearch] = useState("");
  const messagesEndRef = useRef(null);
  const msgContainerRef = useRef(null);
  const scrollToBottom = () => { if (msgContainerRef.current) msgContainerRef.current.scrollTop = msgContainerRef.current.scrollHeight; };

  // ── Contact search for new message ──
  const [contactSearch, setContactSearch] = useState("");
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [selectedNewContact, setSelectedNewContact] = useState(null);
  const contactSearchRef = useRef(null);

  const filteredContacts = useMemo(() => {
    if (!contactSearch || contactSearch.length < 1) return [];
    const s = contactSearch.toLowerCase();
    return CONTACTS.filter(c => {
      const name = ((c.fn || "") + " " + (c.ln || "")).toLowerCase();
      const phone = (c.ph || c.phone || "");
      const email = (c.email || "").toLowerCase();
      const company = (c.co || "").toLowerCase();
      return (name.includes(s) || phone.includes(contactSearch) || email.includes(s) || company.includes(s)) && (c.ph || c.phone);
    }).slice(0, 8);
  }, [contactSearch, CONTACTS]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (contactSearchRef.current && !contactSearchRef.current.contains(e.target)) {
        setShowContactDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Filters ──
  const [view, setView] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [filterTeam, setFilterTeam] = useState("all");
  const [teams, setTeams] = useState([]);
  const [teamMemberships, setTeamMemberships] = useState([]);

  useEffect(() => {
    const orgId = window.__coreflow_org_id;
    if (!orgId) return;
    supabase.from("teams").select("*").eq("org_id", orgId).then(({ data }) => { if (data) setTeams(data); });
    supabase.from("team_members").select("*").then(({ data }) => { if (data) setTeamMemberships(data); });
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const orgId = window.__coreflow_org_id;
      const { data, error } = await supabase.from("sms_messages")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: true })
        .limit(500);
      if (error) { console.warn("[CoreFlow SMS] Load error:", error.message); return; }
      if (data) {
        setMessages(data);
        console.log("[CoreFlow SMS] Loaded", data.length, "messages from DB");
      }
    } catch (err) {
      console.warn("[CoreFlow SMS] Load failed:", err.message);
    }
  }, []);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  const convoList = useMemo(() => {
    const map = {};
    messages.forEach(m => {
      const phone = m.direction === "outbound" ? m.to_number : m.from_number;
      if (!phone) return;
      const clean = phone.replace(/[^\d]/g, "");
      if (!map[clean]) {
        map[clean] = { phone, cleanPhone: clean, messages: [], lastMessage: null, lastDate: null, contact: null };
      }
      map[clean].messages.push(m);
      if (!map[clean].lastDate || new Date(m.created_at) > new Date(map[clean].lastDate)) {
        map[clean].lastDate = m.created_at;
        map[clean].lastMessage = m.body;
      }
    });
    Object.values(map).forEach(c => {
      const match = CONTACTS.find(ct => {
        const ph = (ct.ph || ct.phone || "").replace(/[^\d]/g, "");
        return ph && c.cleanPhone.endsWith(ph);
      });
      if (match) c.contact = match;
    });
    return Object.values(map).sort((a, b) => new Date(b.lastDate) - new Date(a.lastDate));
  }, [messages, CONTACTS]);

  const filteredConvos = useMemo(() => {
    let list = convoList;
    if (view === "unread") {
      list = list.filter(c => {
        const sorted = [...c.messages].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        return sorted[0] && sorted[0].direction === "inbound";
      });
    } else if (view === "sent") {
      list = list.filter(c => c.messages.some(m => m.direction === "outbound"));
    } else if (view === "received") {
      list = list.filter(c => c.messages.some(m => m.direction === "inbound"));
    }
    if (filterUser !== "all") {
      list = list.filter(c => c.messages.some(m => m.user_id === filterUser) || (c.contact && (c.contact.ownerId === filterUser || c.contact._dbId === filterUser)));
    }
    if (filterTeam !== "all") {
      const teamUserIds = teamMemberships.filter(tm => tm.team_id === filterTeam).map(tm => tm.user_id);
      list = list.filter(c => c.messages.some(m => teamUserIds.includes(m.user_id)) || (c.contact && teamUserIds.includes(c.contact.ownerId)));
    }
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(c => {
        const name = c.contact ? (c.contact.fn + " " + c.contact.ln).toLowerCase() : "";
        return name.includes(s) || c.phone.includes(search) || (c.lastMessage || "").toLowerCase().includes(s);
      });
    }
    return list;
  }, [convoList, search, view, filterUser, filterTeam, teamMemberships]);

  useEffect(() => {
    const target = window.__coreflow_notification_target;
    if (target?.category === 'sms' && target.meta?.from && convoList.length > 0) {
      const cleanPhone = (target.meta.from || "").replace(/[^\d]/g, "");
      if (cleanPhone) {
        const match = convoList.find(c => c.cleanPhone.endsWith(cleanPhone.slice(-10)) || cleanPhone.endsWith(c.cleanPhone.slice(-10)));
        if (match) setSelectedConvo(match);
        window.__coreflow_notification_target = null;
      }
    }
  }, [convoList]);

  const selectedMessages = useMemo(() => {
    if (!selectedConvo) return [];
    return messages.filter(m => {
      const phone = m.direction === "outbound" ? m.to_number : m.from_number;
      return phone && phone.replace(/[^\d]/g, "").endsWith(selectedConvo.cleanPhone.slice(-10));
    }).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [messages, selectedConvo]);

  const handleSend = async () => {
    const to = selectedConvo ? selectedConvo.phone : newMessageTo;
    if (!to || !smsBody.trim()) return;
    setSmsSending(true);
    try {
      const ct = selectedConvo?.contact || CONTACTS.find(c => {
        const ph = (c.ph || c.phone || "").replace(/[^\d]/g, "");
        const cleanTo = to.replace(/[^\d]/g, "");
        return ph && cleanTo.endsWith(ph);
      });
      const resp = await apiFetch("/api/sms/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to, body: smsBody, contactId: ct?.id || null, orgId: currentUser?._orgId }),
      });
      const result = await resp.json();
      if (result.success) {
        setSmsBody("");
        setShowNewMessage(false);
        setTimeout(loadMessages, 500);
        if (!selectedConvo) {
          const cleanTo = to.replace(/[^\d]/g, "");
          setSelectedConvo({ phone: to, cleanPhone: cleanTo, contact: ct || null, messages: [], lastMessage: smsBody, lastDate: new Date().toISOString() });
        }
      } else {
        alert("SMS failed: " + (result.error || "Unknown error"));
      }
    } catch (e) {
      console.error("[CoreFlow SMS] Error:", e);
      alert("SMS failed");
    } finally {
      setSmsSending(false);
    }
  };

  const formatPhone = (ph) => {
    const clean = (ph || "").replace(/[^\d]/g, "");
    if (clean.length === 11 && clean.startsWith("1")) return `(${clean.slice(1,4)}) ${clean.slice(4,7)}-${clean.slice(7)}`;
    if (clean.length === 10) return `(${clean.slice(0,3)}) ${clean.slice(3,6)}-${clean.slice(6)}`;
    return ph;
  };

  const formatTime = (d) => {
    const date = new Date(d);
    const mo = String(date.getMonth()+1).padStart(2,"0");
    const dy = String(date.getDate()).padStart(2,"0");
    const yr = String(date.getFullYear()).slice(-2);
    let h = date.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
    const mn = String(date.getMinutes()).padStart(2,"0");
    return mo + "/" + dy + "/" + yr + " " + h + ":" + mn + " " + ap;
  };

  const selectContact = (c) => {
    setSelectedNewContact(c);
    setNewMessageTo(c.ph || c.phone);
    setContactSearch(c.fn + " " + c.ln);
    setShowContactDropdown(false);
  };

  const clearSelectedContact = () => {
    setSelectedNewContact(null);
    setNewMessageTo("");
    setContactSearch("");
  };

  return (
    <div style={{ display: "flex", height: "100%", background: "transparent" }}>
      {/* Conversation List */}
      {(!isMobile || !mobileShowChat) && <div style={{ width: isMobile ? "100%" : 320, borderRight: isMobile ? "none" : "1px solid " + T.bdr, display: "flex", flexDirection: "column", background: "#2C2E33" }}>
        <div style={{ padding: "16px 14px 10px", borderBottom: "1px solid " + T.bdr }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <h2 style={{ fontSize: 16, fontWeight: 400, color: "#FAFAFA", margin: 0 }}>Messages</h2>
            <button onClick={() => { setShowNewMessage(true); setSelectedConvo(null); clearSelectedContact(); if (isMobile) setMobileShowChat(true); }}
              style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, fontFamily: "inherit", cursor: "pointer" }}>New Message</button>
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {[{ label: "Sent", val: messages.filter(m => m.direction === "outbound").length }, { label: "Received", val: messages.filter(m => m.direction === "inbound").length }, { label: "Threads", val: convoList.length }].map(s => (
              <div key={s.label} style={{ flex: 1, padding: "6px 8px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA" }}>{s.val}</div>
                <div style={{ fontSize: 8, color: T.tx3, letterSpacing: ".04em", textTransform: "uppercase" }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            {[{ id: "all", label: "All" }, { id: "unread", label: "Needs Reply" }, { id: "sent", label: "Sent" }, { id: "received", label: "Received" }].map(v2 => (
              <button key={v2.id} onClick={() => setView(v2.id)}
                style={{ flex: 1, padding: "4px 0", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 9, fontFamily: "inherit", background: view === v2.id ? "rgba(64,89,173,0.15)" : "transparent", color: view === v2.id ? "#FAFAFA" : T.tx3 }}>
                {v2.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
              style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "#33363D", color: "#FAFAFA", fontSize: 10, outline: "none", fontFamily: "inherit" }}>
              <option value="all">All Users</option>
              {(TEAM_MEMBERS || []).map(u => (
                <option key={u._dbId || u.id} value={u._dbId || u.id}>{u.name || (u.fn || "") + " " + (u.ln || "")}</option>
              ))}
            </select>
            <select value={filterTeam} onChange={e => setFilterTeam(e.target.value)}
              style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "#33363D", color: "#FAFAFA", fontSize: 10, outline: "none", fontFamily: "inherit" }}>
              <option value="all">All Teams</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search messages..."
            style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(0,0,0,0.15)", color: "#FAFAFA", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ flex: 1, overflow: "auto" }}>
          {filteredConvos.length === 0 && !showNewMessage ? (
            <div style={{ padding: 40, textAlign: "center", color: T.tx3 }}>
              <div style={{ fontSize: 13, fontWeight: 400, marginBottom: 4 }}>No conversations yet</div>
              <div style={{ fontSize: 11 }}>Click "New Message" to start texting</div>
            </div>
          ) : filteredConvos.map(c => {
            const isSelected = selectedConvo?.cleanPhone === c.cleanPhone;
            const name = c.contact ? `${c.contact.fn} ${c.contact.ln}` : formatPhone(c.phone);
            const initials = c.contact ? (c.contact.fn?.[0] || "") + (c.contact.ln?.[0] || "") : "#";
            return (
              <button key={c.cleanPhone} onClick={() => { setSelectedConvo(c); setShowNewMessage(false); if (isMobile) setMobileShowChat(true); }}
                style={{ display: "flex", gap: 10, alignItems: "center", width: "100%", padding: "12px 14px", border: "none", borderBottom: "1px solid " + T.bdr, background: isSelected ? "#4059AD" : "transparent", cursor: "pointer", textAlign: "left" }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.06)", border: "1px solid " + T.bdr, fontSize: 11, fontWeight: 400, color: "#FAFAFA", flexShrink: 0 }}>{initials.toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{name}</span>
                    <span style={{ fontSize: 9, color: T.tx3, flexShrink: 0 }}>{formatTime(c.lastDate)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: T.tx3, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.lastMessage || "No messages"}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>}

      {/* Message Thread / New Message */}
      {(!isMobile || mobileShowChat) && <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {showNewMessage ? (
          <>
            <div style={{ padding: isMobile ? "10px 12px" : "14px 16px", borderBottom: "1px solid " + T.bdr, background: "#2C2E33" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                {isMobile && <button onClick={() => { setMobileShowChat(false); setShowNewMessage(false); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9L11 14" stroke="#FAFAFA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>}
                <span style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>New Message</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: T.tx3, flexShrink: 0 }}>To:</span>
                <div ref={contactSearchRef} style={{ flex: 1, position: "relative" }}>
                  {selectedNewContact ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderRadius: 8, background: "rgba(64,89,173,0.1)", border: "1px solid rgba(64,89,173,0.25)" }}>
                      <div style={{ width: 24, height: 24, borderRadius: 12, background: "rgba(64,89,173,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#4059AD", flexShrink: 0 }}>
                        {(selectedNewContact.fn?.[0] || "") + (selectedNewContact.ln?.[0] || "")}
                      </div>
                      <span style={{ fontSize: 12, color: "#FAFAFA" }}>{selectedNewContact.fn} {selectedNewContact.ln}</span>
                      <span style={{ fontSize: 10, color: T.tx3 }}>{formatPhone(selectedNewContact.ph || selectedNewContact.phone)}</span>
                      <button onClick={clearSelectedContact} style={{ marginLeft: "auto", background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 14, padding: "0 4px", fontFamily: "inherit" }}>×</button>
                    </div>
                  ) : (
                    <>
                      <input
                        value={contactSearch}
                        onChange={e => { setContactSearch(e.target.value); setShowContactDropdown(true); setNewMessageTo(e.target.value); }}
                        onFocus={() => { if (contactSearch.length >= 1) setShowContactDropdown(true); }}
                        placeholder="Search by name, phone, email, or company..."
                        style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.05)", color: "#FAFAFA", fontSize: 12, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                      />
                      {showContactDropdown && filteredContacts.length > 0 && (
                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, maxHeight: 280, overflowY: "auto", zIndex: 20, boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
                          {filteredContacts.map(c => {
                            const initials = (c.fn?.[0] || "") + (c.ln?.[0] || "");
                            return (
                              <button key={c.id} onClick={() => selectContact(c)}
                                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", border: "none", borderBottom: "1px solid rgba(217,219,221,0.06)", background: "transparent", cursor: "pointer", textAlign: "left" }}
                                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                <div style={{ width: 30, height: 30, borderRadius: 15, background: "rgba(64,89,173,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#4059AD", flexShrink: 0 }}>{initials}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, color: "#FAFAFA", fontWeight: 400 }}>{c.fn} {c.ln}</div>
                                  <div style={{ fontSize: 10, color: T.tx3, marginTop: 1 }}>
                                    {formatPhone(c.ph || c.phone)}
                                    {c.co ? " · " + c.co : ""}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {showContactDropdown && contactSearch.length >= 2 && filteredContacts.length === 0 && (
                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, padding: "12px 14px", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, zIndex: 20, boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
                          <div style={{ fontSize: 11, color: T.tx3 }}>No contacts found. You can type a phone number directly.</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ textAlign: "center", color: T.tx3 }}>
                <div style={{ fontSize: 13, fontWeight: 400 }}>Select a contact and type your message below</div>
              </div>
            </div>
          </>
        ) : selectedConvo ? (
          <>
            <div style={{ padding: isMobile ? "10px 12px" : "14px 16px", borderBottom: "1px solid " + T.bdr, background: "#2C2E33", display: "flex", alignItems: "center", gap: 10 }}>
              {isMobile && <button onClick={() => setMobileShowChat(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center", flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9L11 14" stroke="#FAFAFA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>}
              <div style={{ width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.06)", border: "1px solid " + T.bdr, fontSize: 10, fontWeight: 400, color: "#FAFAFA" }}>
                {selectedConvo.contact ? (selectedConvo.contact.fn?.[0] || "") + (selectedConvo.contact.ln?.[0] || "") : "#"}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>
                  {selectedConvo.contact ? `${selectedConvo.contact.fn} ${selectedConvo.contact.ln}` : formatPhone(selectedConvo.phone)}
                </div>
                <div style={{ fontSize: 10, color: T.tx3 }}>{formatPhone(selectedConvo.phone)}</div>
              </div>
            </div>
            <div ref={msgContainerRef} style={{ flex: 1, overflow: "auto", padding: 16, display: "flex", flexDirection: "column-reverse" }}>
              <div>
              {selectedMessages.map((m, i) => {
                const isOut = m.direction === "outbound";
                return (
                  <div key={m.id || i} style={{ display: "flex", justifyContent: isOut ? "flex-end" : "flex-start", marginBottom: 6 }}>
                    <div style={{
                      maxWidth: "65%", padding: "8px 12px", borderRadius: 8,
                      borderBottomRightRadius: isOut ? 4 : 8, borderBottomLeftRadius: isOut ? 8 : 4,
                      background: isOut ? T.blue : "#2C2E33", border: isOut ? "none" : "1px solid rgba(217,219,221,0.10)",
                      color: "#FAFAFA", fontSize: 13, lineHeight: 1.5,
                    }}>
                      <div>{m.body}</div>
                      <div style={{ fontSize: 9, opacity: 0.5, marginTop: 3, textAlign: "right" }}>
                        {(() => { const _d = new Date(m.created_at); let _h = _d.getHours(); const _ap = _h >= 12 ? "PM" : "AM"; _h = _h % 12 || 12; return _h + ":" + String(_d.getMinutes()).padStart(2,"0") + " " + _ap; })()}
                        {isOut && m.status && <span style={{ marginLeft: 4 }}>{m.status === "delivered" ? "Delivered" : m.status === "sent" ? "Sent" : ""}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center", color: T.tx3 }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 4 }}>SMS Inbox</div>
              <div style={{ fontSize: 12 }}>Select a conversation or start a new message</div>
            </div>
          </div>
        )}

        {(selectedConvo || (showNewMessage && newMessageTo)) && (
          <div style={{ padding: "12px", borderTop: "1px solid " + T.bdr, background: "#2C2E33" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <textarea value={smsBody} onChange={e => setSmsBody(e.target.value)}
                placeholder="Type a message..." rows={2}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "8px 12px", color: T.txt, fontSize: 13, outline: "none", resize: "none", lineHeight: 1.4, fontFamily: "inherit" }} />
              <button onClick={handleSend} disabled={smsSending || !smsBody.trim()}
                style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: !smsBody.trim() ? T.bdr : "#4059AD", color: "#D9DBDD", fontSize: 12, fontWeight: 400, cursor: "pointer" }}>
                {smsSending ? "..." : "Send"}
              </button>
            </div>
            <div style={{ padding: "4px 0 0", fontSize: 9, color: T.tx3 }}>
              {smsBody.length}/160 characters {smsBody.length > 160 ? `(${Math.ceil(smsBody.length / 160)} segments)` : ""}
            </div>
          </div>
        )}
      </div>}
    </div>
  );
}
